import { AsyncPipe, NgForOf, NgSwitch } from '@angular/common';
import {
    Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, takeUntil } from 'rxjs/operators';

import { isPlaceholderEnabledUseCase } from '../../../application/use-cases/is-placeholder-enabled.use-case';
import { shouldShowQuicklyUseCase } from '../../../application/use-cases/should-show-quickly.use-case';
import { SetRelatedsCarouselsForPlace } from '../../../infrastructure/state/related-products.actions';
import { getRelatedProductsPlaceCarousels } from '../../../infrastructure/state/related-products.selectors';
import { RelatedProductsCarousel } from '../../../infrastructure/state/related-products.state';
import { RelatedProductsCoreModule } from '../../../related-products-core.module';
import { RelatedProductsModule } from '../../../related-products.module';
import { RelatedProductsCarouselComponent } from '../../components/related-products-carousel/related-products-carousel.component';
import { RelatedProductsGridComponent } from '../../components/related-products-grid/related-products-grid.component';
import { RelatedProductsSkeletonComponent } from '../../components/related-products-skeleton/related-products-skeleton.component';

import { CartItem } from '@api/cart/cart.models';
import { getLanguageisRTL } from '@core/infrastructure/state/core.selectors';
import { getCategoriesNavigation } from '@features/category/infrastructure/state/category.selectors';
import { ProductPublicModels } from '@features/products/product';
import { ProductsArrayFeatureCoreModule } from '@features/products/productsArray/productsArray-feature.core.module';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { MenuCategory } from '@shared/header/models/category.model';
import { ProductViewPlace } from '@shared/product/models';

@Component({
    selector: 'related-products',
    templateUrl: './related-products.component.html',
    standalone: true,
    imports: [
        AsyncPipe,
        NgForOf,
        NgSwitch,
        RelatedProductsCarouselComponent,
        RelatedProductsGridComponent,
        RelatedProductsModule,
        ProductsArrayFeatureCoreModule,
        RelatedProductsCoreModule,
        RelatedProductsSkeletonComponent,
    ],
})
export class RelatedProductsComponent implements OnChanges, OnInit, OnDestroy {
    // #region DEPENDENCIES INJECTION
    private readonly store: Store = inject(Store);
    // #endregion DEPENDENCIES INJECTION

    @Input() public baseProducts: Array<ProductPublicModels.PublicProduct | CartItem>;

    @Input() public place: ProductViewPlace;

    @Input() public excludedProducts: ProductPublicModels.PublicProduct[];

    @Input() public categoryId: string;

    @Input() public hidePriceText?: boolean;

    @Input() public slidesPerView?: number;

    @Input() private readonly section?: string;

    public readonly eventFrom: EventFrom = EventFrom.relateds;

    public carouselExcludedProducts$: Observable<ProductPublicModels.PublicProduct[]>;

    public carousels$: Observable<RelatedProductsCarousel[]>;

    public carouselsChanges$: Observable<Record<string, RelatedProductsCarousel>>;

    public readonly isRtl$: Observable<boolean> = this.store.select(getLanguageisRTL);

    public readonly direction$: Observable<string> = this.isRtl$.pipe(map(isRtl => isRtl ? 'rtl' : 'ltr'));

    public category$: Observable<MenuCategory>;

    private readonly getCategoriesNavigation$ = this.store.select(getCategoriesNavigation);

    private readonly onDestroy$ = new Subject<void>();

    private readonly CAROUSEL_CHANGES_DEBOUCE_TIME = 500;

    public showQuickly: boolean;

    public isPlaceholderEnabled = false;

    // #region ANGULAR LIFECYCLE HOOKS
    public ngOnInit(): void {
        if (this.categoryId) {
            this.category$ = this.getCategoriesNavigation$.pipe(
                map((categories) => categories.find((cat) => this.categoryId === cat.id))
            );
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.baseProducts || changes.place || changes.excludedProducts || changes.section) {
            this.initialize();
        }

        if (changes.place) {
            this.carousels$ = this.store.select(getRelatedProductsPlaceCarousels(this.place))
                .pipe(
                    filter((carousels) => !!carousels),
                    map((carousels) => Object.values(carousels)),
                    distinctUntilChanged((prev, curr) =>
                        JSON.stringify(curr.map((a) => a.itemsIds)) === JSON.stringify(prev.map((a) => a.itemsIds))),
                    filter((carousels) => !!carousels?.find((carousel) => !!carousel?.itemsIds?.length)),
                    shareReplay(1),
                    takeUntil(this.onDestroy$),
                );

            this.carouselsChanges$ = this.store.select(getRelatedProductsPlaceCarousels(this.place)).pipe(
                debounceTime(this.CAROUSEL_CHANGES_DEBOUCE_TIME),
                filter((carousels) => !!carousels),
                map((carousels) => this.limitProductsShown(carousels)),
                takeUntil(this.onDestroy$),
            );

            this.isPlaceholderEnabled = isPlaceholderEnabledUseCase(this.place);
        }
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    // #endregion ANGULAR LIFECYCLE HOOKS

    private initialize() {
        const baseProducts = (this.baseProducts as ProductPublicModels.PublicProduct[])?.[0]?.bundleProducts?.length
            ? (this.baseProducts as ProductPublicModels.PublicProduct[])[0].bundleProducts : this.baseProducts;
        this.store.dispatch(SetRelatedsCarouselsForPlace({ placeName: this.place, baseProducts, section: this.section }));
        this.showQuickly = shouldShowQuicklyUseCase(this.place);
    }

    private limitProductsShown(carousels: Record<string, RelatedProductsCarousel>): Record<string, RelatedProductsCarousel> {
        const _carousels = Object.entries(carousels);
        return _carousels.reduce((acc, [key, carousel]) => {
            const maxProductsCarousel = carousel?.relatedTypes?.reduce((_acc, relatedType) => {
                return relatedType?.maxItems + _acc;
            }, 0);
            acc[key] = {
                ...carousel,
                filteredItemsIds: carousel?.filteredItemsIds?.slice(0, maxProductsCarousel),
            };
            return acc;
        }, {});
    }
}
